﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import type { MutationDeleteSubscriptionPlanArgs } from "@/types/graphql";

/**
 * Custom hook to delete a subscription plan.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteSubscriptionPlan: Function, deleting: boolean}} - The function to delete a subscription plan and the loading state.
 */
export function useDeleteSubscription(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  // Get the mutation function and the loading state from the Apollo client.
  const [deleteSubscriptionPlan, { loading }] = useMutation<
    any,
    MutationDeleteSubscriptionPlanArgs
  >(mutations.deleteSubscriptionPlan, {
    // The callback function to be called when the mutation completes successfully.
    onCompleted,
    // The callback function to be called when an error occurs during the mutation.
    onError: (err) => {
      onError(err);

      // Show the error message in a snackbar.
      enqueueSnackbar("Delete Subscription Plan: " + err.message, {
        variant: "error",
      });
    },
    // Set the error policy to "all", so that errors are not thrown away.
    errorPolicy: "all",
  });

  // Create a function to call the mutation with the correct variables.
  const deleteSubscriptionPlanRow = useCallback(
    (variables: MutationDeleteSubscriptionPlanArgs) => {
      // Call the mutation function with the variables and refetch the "getAllRoles" query.
      deleteSubscriptionPlan({
        variables,
        refetchQueries: ["GetSubscriptionPlans"],
      }).then();
    },
    [deleteSubscriptionPlan],
  );

  // Return the function to delete a subscription plan and the loading state.
  return {
    deleteSubscriptionPlan: deleteSubscriptionPlanRow,
    deleting: loading,
  };
}
